import * as actions from '../actions';
import * as constants from '../constants';
import {
  Message, Messages,
  MessageState, initialMessageState,
  MessageCategory, MessageCategoryAttributes,
  StatusMessage,
} from '../models';


const getMessageCategoryDataState =
  (state: MessageState, action: actions.MessageCategoryData) => {
    const sortedCategories = [...action.messageCategories]
      .sort((mc1: MessageCategory, mc2: MessageCategory) => {
        return mc1.displayOrder - mc2.displayOrder;
      });
    return { ...state, messageCategories: sortedCategories };
  };

const compareFn = (num1: number, num2: number): number => {
  return num1 - num2;
};
const sortMessagesInACategory = (m1: Message, m2: Message, categoryId: number) => {
  const c1 = m1.categories.find(c => c.id === categoryId);
  const c2 = m2.categories.find(c => c.id === categoryId);
  if (c1 && c2) {
    return compareFn(c1.displayOrder, c2.displayOrder);
  }
  return 0;
};

const getCategories = (messageCategories: MessageCategory[], messages: Messages) => {
  const updatedMessageCategories = messageCategories.map((mc: MessageCategory) => {
    return {
      ...mc,
      messages: messages.filter(m => m.categories.find(c => c.id === mc.id))
        .sort((m1: Message, m2: Message) => sortMessagesInACategory(m1, m2, (mc.id || 0))),
    };
  });
  return updatedMessageCategories;
};

const getViewMessageSuccessState =
  (state: MessageState, action: actions.ViewMessagesSuccess) => {
    const messageCategories = getCategories(state.messageCategories, action.messages);
    const _messages = [...action.messages].sort((m1: Message, m2: Message) => {
      return (m1.displayOrder || 0) - (m2.displayOrder || 0);
    });

    return {
      ...state, messageCategories,
      _messages, 
      messages: [..._messages],
      isAppLoaded: true,
    };
  };

const showAddMessageSectionState =
  (state: MessageState, action: actions.ShowAddMessageSection) => {
    return { ...state, isAddMessagesInProgress: true };
  };

const addMessageSuccessState =
  (state: MessageState, action: actions.AddMessageSuccess) => {
    const _messages = [...state._messages, action.message];
    const messages = [...state.messages, action.message];

    action.message.categories.forEach((c: MessageCategoryAttributes) => {
      const messageCateory = state.messageCategories.find(mc => mc.id === c.id);
      if (messageCateory) {
        messageCateory.messages = [...messageCateory.messages, action.message];
      }
    });

    return {
      ...state,
      _messages,
      messages,
      isAddMessagesInProgress: false,
      messageCategories: [...state.messageCategories],
      statusMessage: constants.messageSaveSuccessful,
      apiError: undefined,
    };
  };

const updateInMemorySorting = (state: MessageState, action: actions.SortMessageInMemory) => {
  if (!state.messageCategories) {
    return state;
  }

  const messageCategories = [...state.messageCategories];
  const messageCategory = messageCategories
    .find(mc => mc.categoryName === action.categoryName);

  if (messageCategory) {
    messageCategory.messages = [...action.messages];
    return {
      ...state,
      messageCategories,
    };
  } else {
    return {
      ...state,
      _messages: [...action.messages],
      messages: [...action.messages],
    };
  }

};

const updatedMessageInAList = (messages: Messages, message: Message) => {
  return messages.map((m: Message) => {
    if (m.id === message.id) {
      return message;
    }
    return m;
  });
};


const editMessageSuccessState = (state: MessageState, action: actions.EditMessageSuccess) => {
  const _messages = updatedMessageInAList(state._messages, action.message);
  const messages = updatedMessageInAList(state.messages, action.message);

  const messageCategories = [...state.messageCategories];
  let isMessageExistsInCategories = false;

  messageCategories.filter((mc: MessageCategory) => {
    return mc.messages.find(m => m.id === action.message.id);
  }).forEach((mc: MessageCategory) => {
    isMessageExistsInCategories = true;
    if (action.message.categories.find(c => c.id === mc.id)) {
      if (mc.messages.find(m => m.id === action.message.id)) {
        mc.messages = updatedMessageInAList(mc.messages, action.message);
      } else {
        mc.messages = [action.message];
      }
    } else {
      mc.messages = mc.messages.filter(m => m.id !== action.message.id);
    }
  });

  if (!isMessageExistsInCategories) {
    action.message.categories.forEach((c: MessageCategoryAttributes) => {
      const messageCategory = messageCategories.find(mc => mc.id === c.id);
      if (messageCategory) {
        messageCategory.messages = [...messageCategory.messages, action.message];
      }
    });
  } else {
    messageCategories.filter((mc: MessageCategory) => {
      return action.message.categories.find(c => c.id !== undefined && c.id === mc.id);
    }).forEach((mc: MessageCategory) => {
      if (mc.messages.find(m => action.message.id === m.id)) {
        mc.messages = updatedMessageInAList(mc.messages, action.message);
      } else {
        mc.messages = [...mc.messages, action.message];
      }
    });
  }

  return { ...state, _messages, messages, messageCategories,
    statusMessage: constants.messageSaveSuccessful };
};


const searchMessageSuccessState =
  (state: MessageState, action: actions.SearchMessageSuccess) => {
    const messageCategories = getCategories(state.messageCategories, action.messages);
    return {
      ...state,
      messageCategories,
      _messages: action.messages,
      isSearchLoading: false,
    };

  };

const getFailureStatusMessage = (error: string): StatusMessage => {
  return { ...constants.messageError, message: error };
};

const removeMessage = (messages: Messages, messageId: number) => {
  return messages.filter(m => m.id !== messageId);
};

const deleteMessageSuccessState = (state: MessageState, action: actions.DeleteMessageSuccess) => {
  const messageCategories = [...state.messageCategories];
  messageCategories
    .filter(mc => mc.messages.find(m => m.id === action.messageId))
    .forEach(mc => mc.messages = removeMessage(mc.messages, action.messageId));
  const messages = removeMessage(state.messages, action.messageId);
  const _messages = removeMessage(state._messages, action.messageId);

  return {
    ...state,
    messageCategories,
    messages,
    _messages,
    statusMessage: constants.messageDeleteSuccessful,
  };

};


export const messageReducer =
  (state: MessageState = initialMessageState, action: actions.MessageAction): MessageState => {

    switch (action.type) {
      case constants.MESSAGES_CATEGORY_DATA:
        return getMessageCategoryDataState(state, action);
      case constants.VIEW_MESSAGES_SUCCESS:
        return getViewMessageSuccessState(state, action);
      case constants.VIEW_MESSAGES_FAILURE:
        return { ...state, statusMessage: getFailureStatusMessage(action.error) };

      case constants.SHOW_ADD_MESSAGE_SECTION:
        return showAddMessageSectionState(state, action);
      case constants.HIDE_ADD_MESSAGE_SECTION:
        return { ...state, isAddMessagesInProgress: false };
      case constants.ADD_MESSAGE_REQUEST:
        return { ...state, isAddMessagesInProgress: false, statusMessage: constants.messageSaving };
      case constants.ADD_MESSAGE_SUCCESS:
        return addMessageSuccessState(state, action);
      case constants.ADD_MESSAGE_FAILURE:
        return { ...state, statusMessage: getFailureStatusMessage(action.error) };

      case constants.SHOW_EDIT_MESSAGES_VIEW:
        return { ...state, isEditMessagesInProgress: true };
      case constants.HIDE_EDIT_MESSAGES_VIEW:
        return { ...state, isEditMessagesInProgress: false };
      case constants.EDIT_MESSAGE_REQUEST:
        return { ...state, statusMessage: constants.messageSaving };
      case constants.EDIT_MESSAGE_SUCCESS:
        return editMessageSuccessState(state, action);
      case constants.EDIT_MESSAGE_FAILURE:
        return { ...state, statusMessage: getFailureStatusMessage(action.error) };

      case constants.SORT_MESSAGES_INMEMORY:
        return updateInMemorySorting(state, action);
      case constants.CATEGORY_SORT_MESSAGES_INMEMORY:
        return { ...state, messageCategories: [...action.messageCategories] };

      case constants.SEARCH_MESSAGE_REQUEST:
        let hasSearchQuery = true;
        if (!action.text || action.text.length === 0) {
          hasSearchQuery = false;
        }
        return { ...state, hasSearchQuery, isSearchLoading: true };
      case constants.SEARCH_MESSAGE_SUCCESS:
        return searchMessageSuccessState(state, action);

      case constants.DELETE_MESSAGE_SUCCESS:
        return deleteMessageSuccessState(state, action);
      case constants.DELETE_MESSAGE_FAILURE:
        return { ...state, statusMessage: getFailureStatusMessage(action.error) };
      case constants.DELETE_MESSAGE_REQUEST:
        return { ...state, statusMessage: constants.messageDeleting };
        
      case constants.DISMISS_MESSAGE_BANNER:
        return { ...state, statusMessage: undefined };
      default:
        return state;
    }

  };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/reducers/messageReducer.ts